import {
  OreHeading,
  OreInput,
  OreMap,
  OreMapMarker,
  OreMessageBar,
  OreStack,
} from '@edenredespana/oreneta'
import { useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AutoEnrollmentTranslation } from '@/AutoEnrollment/ui/autoEnrollmentTranslation'
import { Step2Form } from '@/AutoEnrollment/ui/hooks'
import { handleErrors } from '@/Shared/ui/form'
import { TransformObject, useGooglePlaces } from '@/Store/ui/hooks'

export const Step2AddressStoreData = (): JSX.Element => {
  const { t, i18n } = useTranslation()

  const formContext = useFormContext<Step2Form>()
  const { register, formState, getValues } = formContext
  const { errors } = handleErrors(formState)
  const { ref, ...rest } = register('storeStreet')

  const transformObject: TransformObject<Step2Form> = {
    street: 'storeStreet',
    number: 'storeNumber',
    floorBlockStair: 'storeFloorBlockStair',
    postalCode: 'storePostalCode',
    city: 'storeCity',
    province: 'storeProvince',
    latitude: 'storeLatitude',
    longitude: 'storeLongitude',
  }

  const inputReference = useRef<HTMLInputElement | null>(null)
  const { initGooglePlaces } = useGooglePlaces({ formContext, transformObject })

  useEffect(() => {
    initGooglePlaces(inputReference)
  }, [])

  return (
    <OreStack placeContent="stretch" space="large">
      <OreHeading as="h2" size="headline-md">
        {t(AutoEnrollmentTranslation.storeAddressData.title)}
      </OreHeading>

      <OreMessageBar color="warning">
        {t(AutoEnrollmentTranslation.storeAddressData.warning)}
      </OreMessageBar>

      <OreStack direction="row" space="large" placeContent="stretch">
        <OreInput
          {...rest}
          {...errors('storeStreet')}
          id="storeStreet"
          label={t(AutoEnrollmentTranslation.storeAddressData.data.street.label)}
          placeholder={t(
            AutoEnrollmentTranslation.storeAddressData.data.street.placeholder,
          )}
          required
          ref={(input: HTMLInputElement) => {
            inputReference.current = input
            ref(input)
          }}
          defaultValue={formContext.getValues('storeStreet') ?? ''}
          labelTooltip={t(AutoEnrollmentTranslation.storeAddressData.data.street.tooltip)}
          autoComplete={'off'}
          role="presentation"
          maxLength={255}
        />
      </OreStack>
      <OreStack direction="row" space="large" placeContent="stretch" sameSize>
        <OreInput
          {...register('storeNumber')}
          {...errors('storeNumber')}
          id="storeNumber"
          label={t(AutoEnrollmentTranslation.storeAddressData.data.number.label)}
          disabled
        />
        <OreInput
          {...register('storeFloorBlockStair')}
          {...errors('storeFloorBlockStair')}
          id="storeFloorBlockStair"
          label={t(AutoEnrollmentTranslation.storeAddressData.data.floorBlockStair.label)}
          disabled
        />
      </OreStack>
      <OreStack direction="row" space="large" placeContent="stretch" sameSize>
        <OreInput
          {...register('storePostalCode')}
          {...errors('storePostalCode')}
          id="storePostalCode"
          label={t(AutoEnrollmentTranslation.storeAddressData.data.postalCode.label)}
          disabled
        />
        <OreInput
          {...register('storeCity')}
          {...errors('storeCity')}
          id="storeCity"
          label={t(AutoEnrollmentTranslation.storeAddressData.data.city.label)}
          disabled
        />
        <OreInput
          {...register('storeProvince')}
          {...errors('storeProvince')}
          id="storeProvince"
          label={t(AutoEnrollmentTranslation.storeAddressData.data.province.label)}
          disabled
        />
      </OreStack>
      <OreStack direction="row" space="large" placeContent="stretch">
        <OreInput
          {...register('storeAdditionalInfo')}
          {...errors('storeAdditionalInfo')}
          id="storeAdditionalInfo"
          label={t(AutoEnrollmentTranslation.storeAddressData.data.additionalInfo.label)}
          placeholder={t(
            AutoEnrollmentTranslation.storeAddressData.data.additionalInfo.placeholder,
          )}
          maxLength={255}
        />
      </OreStack>
      {getValues('storeLatitude') && getValues('storeLongitude') ? (
        <div
          style={{
            height: '260px',
            width: '100%',
            position: 'relative',
          }}
        >
          <OreMap
            loaderOptions={{
              apiKey: import.meta.env.VITE_GOOGLE_KEY,
              language: i18n.language,
              region: 'ES',
            }}
            center={{
              lat: Number(getValues('storeLatitude')),
              lng: Number(getValues('storeLongitude')),
            }}
            zoom={16}
            mapOptions={{
              disableDefaultUI: true,
              mapTypeControl: false,
              zoomControl: true,
              panControl: false,
              disableDoubleClickZoom: true,
              gestureHandling: 'none',
            }}
          >
            <OreMapMarker
              position={{
                lat: Number(getValues('storeLatitude')),
                lng: Number(getValues('storeLongitude')),
              }}
              title={getValues('storeName')}
              onClick={marker => {
                const infoWindow = new google.maps.InfoWindow({
                  content: getValues('storeName'),
                  ariaLabel: getValues('storeName'),
                })

                infoWindow.open({
                  anchor: marker,
                  map: marker.map,
                })
              }}
            />
          </OreMap>
        </div>
      ) : null}
    </OreStack>
  )
}
